import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface SettingsData {
  allowSignups: boolean;
  requireEmailVerification: boolean;
  sessionTimeout: number;
  maxApiTokens: number;
  allowedDomains: string[];
}

function SettingsPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/admin/settings`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      return response.json();
    },
  });

  const [settings, setSettings] = useState<SettingsData>({
    allowSignups: true,
    requireEmailVerification: false,
    sessionTimeout: 10080,
    maxApiTokens: 10,
    allowedDomains: [],
  });

  useEffect(() => {
    if (data?.settings) {
      setSettings(data.settings);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (settings: SettingsData) => {
      const response = await fetch(`${API_URL}/v1/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(settings),
      });
      if (!response.ok) throw new Error('Failed to save settings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    },
  });

  const subNavItems = [
    { to: '/admin', label: 'Teams' },
    { to: '/admin/settings', label: 'Settings' },
    { to: '/admin/billing', label: 'Billing' },
    { to: '/admin/audit', label: 'Audit' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">System Settings</h1>
        <p className="text-muted-foreground">Configure your organization settings</p>
      </div>

      <SubNav items={subNavItems} />

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {message && (
        <div
          className={`flex items-center gap-2 rounded-lg p-4 ${
            message.type === 'success'
              ? 'bg-green-500/15 text-green-400'
              : 'bg-destructive/15 text-destructive'
          }`}
        >
          {message.type === 'success' && <Check className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {!isLoading && (
        <div className="max-w-2xl space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle>Authentication</CardTitle>
                  <CardDescription>Configure how users access your organization</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Allow Signups</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow new users to create accounts
                  </p>
                </div>
                <Switch
                  checked={settings.allowSignups}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, allowSignups: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Require Email Verification</Label>
                  <p className="text-sm text-muted-foreground">
                    Users must verify their email before accessing the app
                  </p>
                </div>
                <Switch
                  checked={settings.requireEmailVerification}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, requireEmailVerification: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Session & Limits</CardTitle>
              <CardDescription>Configure session timeout and resource limits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) =>
                    setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) || 0 })
                  }
                  min={5}
                  max={43200}
                  className="w-40"
                />
                <p className="text-xs text-muted-foreground">
                  How long until inactive sessions expire (5 - 43200 minutes)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxApiTokens">Max API Tokens per User</Label>
                <Input
                  id="maxApiTokens"
                  type="number"
                  value={settings.maxApiTokens}
                  onChange={(e) =>
                    setSettings({ ...settings, maxApiTokens: parseInt(e.target.value) || 0 })
                  }
                  min={1}
                  max={100}
                  className="w-40"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of API tokens each user can create (1 - 100)
                </p>
              </div>
            </CardContent>
          </Card>

          <Button onClick={() => saveMutation.mutate(settings)} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/settings')({
  component: SettingsPage,
});
